import { applyPct } from "./arith";
import { DomainError } from "./errors";

/** Comissão de 0% a 100% (pct4). */
export const MAX_COMMISSION_PCT4 = 1_000_000;
/** Limites por empresa (como listas de preço e condições): 422 `limit_reached`. */
export const MAX_SELLERS_PER_COMPANY = 500;
export const MAX_COMMISSION_RULES_PER_COMPANY = 2_000;
/** Faixa de desconto concedido: 0% a 100% (pct4). */
export const MAX_DISCOUNT_PCT4 = 1_000_000;

export const COMMISSION_BASES = ["invoiced", "received"] as const;
export type CommissionBase = (typeof COMMISSION_BASES)[number];

export type CommissionRule = {
  id: string;
  /** null = todos os vendedores. */
  sellerId: string | null;
  productId: string | null;
  categoryId: string | null;
  /** Faixa do desconto concedido no item, inclusiva; null = aberta naquela ponta. */
  discountFromPct4: number | null;
  discountToPct4: number | null;
  commissionPct4: number;
  /** Desempate dentro do mesmo nível: maior primeiro. */
  priority: number;
};

/**
 * Nível da precedência (05 §6), do mais forte (1) ao padrão do vendedor (6):
 * 1 vendedor + produto · 2 vendedor + categoria · 3 vendedor + faixa · 4 produto · 5 categoria · 6 padrão.
 */
export type CommissionLevel = 1 | 2 | 3 | 4 | 5 | 6;

export type CommissionResolution = {
  pct4: number;
  source: "rule" | "default";
  level: CommissionLevel;
  ruleId: string | null;
};

export type ResolveCommissionInput = {
  /** Vendedor do documento; null = venda sem vendedor (só regras gerais). */
  sellerId: string | null;
  /** Comissão padrão do vendedor (pct4). */
  defaultPct4: number;
  /** Produto do item e os seus "pais" (variação → produto pai), do mais próximo ao mais distante. */
  productIds: readonly string[];
  /** Categoria do produto e as ancestrais, da mais próxima à mais distante. */
  categoryIds: readonly string[];
  /** Desconto concedido no item (pct4). */
  discountPct4: number;
  rules: readonly CommissionRule[];
};

const assertInt = (v: number, what: string) => {
  if (!Number.isSafeInteger(v)) throw new DomainError("not_integer", `${what} deve ser inteiro: ${v}`);
};

const inBand = (r: CommissionRule, d: number) =>
  (r.discountFromPct4 == null || d >= r.discountFromPct4) &&
  (r.discountToPct4 == null || d <= r.discountToPct4);

const hasBand = (r: CommissionRule) => r.discountFromPct4 != null || r.discountToPct4 != null;

/**
 * Nível e distância (posição em productIds/categoryIds) da regra para o item, ou null quando não casa.
 * Regra geral sem produto e sem categoria (só faixa) não está na precedência do spec: não casa.
 */
function match(
  r: CommissionRule,
  i: ResolveCommissionInput,
): { level: CommissionLevel; distance: number } | null {
  if (!inBand(r, i.discountPct4)) return null;
  const own = r.sellerId !== null;
  if (own && r.sellerId !== i.sellerId) return null;
  if (r.productId !== null) {
    const distance = i.productIds.indexOf(r.productId);
    return distance < 0 ? null : { level: own ? 1 : 4, distance };
  }
  if (r.categoryId !== null) {
    const distance = i.categoryIds.indexOf(r.categoryId);
    return distance < 0 ? null : { level: own ? 2 : 5, distance };
  }
  return own ? { level: 3, distance: 0 } : null;
}

/**
 * Percentual de comissão do item (05 §6): (vendedor + produto) > (vendedor + categoria) > (vendedor + faixa de
 * desconto) > (produto) > (categoria) > padrão do vendedor. A faixa, quando existe, é condição extra em qualquer nível.
 * No mesmo nível vence o produto/categoria mais próximo, depois a maior `priority`, depois a regra com faixa (mais
 * específica) e por fim o menor id (determinístico).
 */
export function resolveCommission(input: ResolveCommissionInput): CommissionResolution {
  assertInt(input.defaultPct4, "comissão padrão");
  assertInt(input.discountPct4, "desconto");
  let best: { rule: CommissionRule; level: CommissionLevel; distance: number } | null = null;
  for (const rule of input.rules) {
    assertInt(rule.commissionPct4, "comissão");
    const m = match(rule, input);
    if (!m) continue;
    if (!best || better({ rule, ...m }, best)) best = { rule, ...m };
  }
  if (!best) return { pct4: input.defaultPct4, source: "default", level: 6, ruleId: null };
  return { pct4: best.rule.commissionPct4, source: "rule", level: best.level, ruleId: best.rule.id };
}

type Candidate = { rule: CommissionRule; level: CommissionLevel; distance: number };
function better(a: Candidate, b: Candidate): boolean {
  if (a.level !== b.level) return a.level < b.level;
  if (a.distance !== b.distance) return a.distance < b.distance;
  if (a.rule.priority !== b.rule.priority) return a.rule.priority > b.rule.priority;
  if (hasBand(a.rule) !== hasBand(b.rule)) return hasBand(a.rule);
  return a.rule.id < b.rule.id;
}

/** Problema de formato da regra (percentual e faixa), ou null. Usado pelo contrato e pelo DO. */
export function commissionRuleProblem(r: {
  commissionPct4: number;
  discountFromPct4?: number | null;
  discountToPct4?: number | null;
}): string | null {
  if (r.commissionPct4 < 0 || r.commissionPct4 > MAX_COMMISSION_PCT4)
    return "A comissão deve ficar entre 0% e 100%";
  const band = [r.discountFromPct4, r.discountToPct4];
  if (band.some((v) => v != null && (v < 0 || v > MAX_DISCOUNT_PCT4)))
    return "A faixa de desconto vai de 0% a 100%";
  if (r.discountFromPct4 != null && r.discountToPct4 != null && r.discountFromPct4 > r.discountToPct4)
    return "O início da faixa deve ser menor ou igual ao fim";
  return null;
}

const nonNegative = (v: number, what: string) => {
  assertInt(v, what);
  if (v < 0) throw new DomainError("invalid_money", `${what} não pode ser negativo: ${v}`);
};

/**
 * Rateio de `amount` (centavos) proporcional aos pesos (05 §3): cada parte é o piso da proporção e a diferença de
 * centavos vai para o item de maior peso (o primeiro, no empate). Pesos todos zero: tudo no primeiro. BigInt.
 */
export function allocateProportional(amount: number, weights: readonly number[]): number[] {
  nonNegative(amount, "valor");
  weights.forEach((w) => nonNegative(w, "peso"));
  if (weights.length === 0) return [];
  const total = weights.reduce((a, w) => a + BigInt(w), 0n);
  const parts = weights.map((w) => (total === 0n ? 0 : Number((BigInt(amount) * BigInt(w)) / total)));
  let largest = 0;
  weights.forEach((w, i) => {
    if (w > weights[largest]!) largest = i;
  });
  parts[largest]! += amount - parts.reduce((a, b) => a + b, 0);
  return parts;
}

const assertPct = (pct4: number) => {
  assertInt(pct4, "comissão");
  if (pct4 < 0 || pct4 > MAX_COMMISSION_PCT4)
    throw new DomainError("invalid_commission", `comissão fora de 0% a 100%: ${pct4}`);
};

/** Comissão do item (05 §6): `applyPct(total − rateioDesconto, pct)`, base nunca negativa. */
export function itemCommission(totalCents: number, discountShareCents: number, pct4: number): number {
  assertPct(pct4);
  return applyPct(Math.max(0, totalCents - discountShareCents), pct4);
}

export type OrderCommissionItem = { totalCents: number; pct4: number };
export type OrderCommissionLine = { discountShareCents: number; baseCents: number; commissionCents: number };

/** Comissão de cada item com o desconto do pedido rateado pelo total dos itens. */
export function orderCommissions(
  items: readonly OrderCommissionItem[],
  orderDiscountCents: number,
): { items: OrderCommissionLine[]; totalCents: number } {
  const shares = allocateProportional(
    orderDiscountCents,
    items.map((i) => Math.max(0, i.totalCents)),
  );
  const lines = items.map((it, k) => {
    const discountShareCents = shares[k]!;
    const commissionCents = itemCommission(it.totalCents, discountShareCents, it.pct4);
    return {
      discountShareCents,
      baseCents: Math.max(0, it.totalCents - discountShareCents),
      commissionCents,
    };
  });
  // Soma em BigInt: muitos itens grandes passariam de 2^53 sem erro em number.
  const total = lines.reduce((a, l) => a + BigInt(l.commissionCents), 0n);
  if (total > BigInt(Number.MAX_SAFE_INTEGER))
    throw new DomainError("invalid_money", `valor fora do limite: ${total}`);
  return { items: lines, totalCents: Number(total) };
}

/**
 * Base "recebido" (05 §6): comissão liberada até agora, proporcional ao pago (`paid / total`), meio para cima e com
 * teto na comissão. É acumulada: cada baixa libera `released(depois) − released(antes)`, sem sobra de centavos.
 * Título de total zero não tem o que receber: libera tudo.
 */
export function releasedCommission(commissionCents: number, paidCents: number, totalCents: number): number {
  nonNegative(commissionCents, "comissão");
  nonNegative(paidCents, "pago");
  nonNegative(totalCents, "total");
  if (totalCents === 0 || paidCents >= totalCents) return commissionCents;
  const num = BigInt(commissionCents) * BigInt(paidCents);
  const den = BigInt(totalCents);
  const q = num / den;
  return Number(2n * (num % den) >= den ? q + 1n : q);
}
